import { NavLink } from 'react-router-dom';
import { SectionIcon, type Section } from './icons';

/**
 * Explore: one section, three tabs (specification §3).
 *
 * The specification asks for the separate public destinations — Find Your Next
 * Home, Services, Marketplace — to become tabs under a single Explore heading,
 * so the public reads them as parts of one platform rather than as unrelated
 * sites.
 *
 * WHAT IT DELIBERATELY DOES NOT DO is move /homes.
 *
 * That URL is indexed, is in the sitemap, and was the subject of a fortnight of
 * SEO work; putting the same listings at /explore/homes would either break
 * those links or publish the same page at two addresses, which is worse. So
 * Explore is a navigational idea rather than a URL prefix: the tabs sit on top
 * of the pages that already exist, at the addresses they already have, and the
 * header offers one Explore item instead of three.
 *
 * Marketplace was shown here as a tab marked "Soon" before it was built, which
 * was right at the time and then outlived the truth: the pages shipped and the
 * flag stayed, so the section existed, answered at /marketplace, held real
 * listings — and the only way in from the public site was an unclickable
 * <span> announcing it as coming. Nothing failed and nothing logged; the
 * feature was simply unreachable. A "Soon" flag is a claim about the product
 * and has to be deleted the day it stops being one.
 *
 * The strip also carries the way IN. Posting is the one thing a visitor cannot
 * discover by browsing — every other action on these pages is something to
 * read — and the header has no room for another item, so it sits at the end of
 * the tabs, on all three sections, pointing at §12's single door.
 */

interface Tab {
  to: string;
  label: string;
  /** The hue and glyph this section owns everywhere it appears. */
  section: Section;
  /** Also lit when the reader is deeper inside this section. */
  match?: (path: string) => boolean;
  soon?: boolean;
}

const TABS: Tab[] = [
  {
    to: '/homes',
    label: 'Find your next home',
    section: 'homes',
    match: (p) => p === '/homes' || p.startsWith('/homes/'),
  },
  {
    // Beside homes rather than after Marketplace: renting and buying are the
    // same question asked with different money, and a visitor who wants to buy
    // should not have to read past two unrelated sections to find out we do
    // it. Top-level /buy, never /explore/buy — see the note at the head of
    // this file on why these URLs do not nest.
    to: '/buy',
    label: 'Buy property',
    section: 'buy',
    match: (p) => p === '/buy' || p.startsWith('/buy/'),
  },
  {
    to: '/services',
    label: 'Services',
    section: 'services',
    match: (p) => p === '/services' || p.startsWith('/services/'),
  },
  {
    to: '/marketplace',
    label: 'Marketplace',
    section: 'market',
    match: (p) => p === '/marketplace' || p.startsWith('/marketplace/'),
  },
];

export function ExploreTabs({ path }: { path: string }) {
  return (
    <nav className="explore-tabs" aria-label="Explore">
      {TABS.map((t) => {
        const active = t.match?.(path) ?? path === t.to;
        if (t.soon) {
          return (
            <span key={t.to} className="explore-tab is-soon" aria-disabled="true">
              {t.label}<span className="explore-soon">Soon</span>
            </span>
          );
        }
        return (
          <NavLink
            key={t.to}
            to={t.to}
            className={`explore-tab explore-tab--${t.section}${active ? ' active' : ''}`}
            aria-current={active ? 'page' : undefined}
          >
            {/* Flat rather than tiled: three coloured squares in a row would
                shout, and the tab strip only needs the hue to be recognisable. */}
            <SectionIcon section={t.section} flat size={17} />
            {t.label}
          </NavLink>
        );
      })}

      {/* Not a tab: it goes somewhere else and does something, so it is styled
          as an action and sits apart from the three sections. */}
      <NavLink to="/post" className="explore-post">
        <SectionIcon section="post" flat size={17} />
        Post something
      </NavLink>
    </nav>
  );
}
